import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from 'hooks';
import type { Filters } from 'hooks/store/prioritized-products';
import {
  useBlockedProductReasonTypeOptions,
  useBlockedProductValueRangeOptions,
  useBlockedProductExpirationRangeOptions,
  usePrioritizedProductDescriptionOptions,
  usePrioritizedProductPriorityOptions,
  useDestinationPlantOptions,
} from 'hooks/api/options';
import { usePlannerOptions } from 'hooks/api/options/planners';
import { Form, FormField } from 'components/ui/form';
import { InputGs, ComboboxGs, MultiSelectGs, DateRangePickerGs } from 'components/ui/Forms';
import { Button as ButtonGs } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { createFilterParams } from './utils';

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
};

const SELECTED_BADGE_CLASS_NAME =
  'md:max-w-[200px] lg:max-w-[180px] xl:max-w-[150px]';

export const PrioritizedProductsFilter = ({
                                            onResetFilters,
                                            onSubmitFilters,
                                          }: Props) => {
  const { profile } = useAuth();
  const reasonOptions = useBlockedProductReasonTypeOptions();
  const valueRangeOptions = useBlockedProductValueRangeOptions();
  const expirationRangeOptions = useBlockedProductExpirationRangeOptions();
  const descriptionOptions = usePrioritizedProductDescriptionOptions();
  const priorityOptions = usePrioritizedProductPriorityOptions();
  const plannerOptions = usePlannerOptions();
  const destinationPlantOptions = useDestinationPlantOptions();

  const showPlannerSelector = React.useMemo(() => {
    if (!profile?.type) {
      return false;
    }
    return ['admin'].includes(profile.type as string);
  }, [profile?.type]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const resetForm = React.useCallback(() => {
    form.reset({
      material: '',
      plannerId: undefined,
      materialDescription: undefined,
      batch: '',
      productReasonTypeId: undefined,
      totalValue: '',
      shelfLifeExpirationDate: undefined,
      storageLocation: '',
      priority: [],
      destinationPlant: undefined,
      deliveryForecastDate: {
        from: undefined,
        to: undefined,
      },
    }, { keepValues: false });
    onResetFilters();
  }, [form, onResetFilters]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    const filters = createFilterParams(formData);
    onSubmitFilters(filters);
  }, [onSubmitFilters]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {showPlannerSelector && (
            <FormField
              control={form.control}
              name={'plannerId'}
              render={({ field }) => (
                <ComboboxGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  content={plannerOptions.singleSelectOptions}
                  label={'Planejador'}
                  clearable
                  portal={false}
                />
              )}
            />
          )}
          <FormField
            control={form.control}
            name={'materialDescription'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={descriptionOptions.singleSelectOptions}
                label={'Descrição do produto'}
                clearable
                portal={false}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'material'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'Código do produto'} />
            )}
          />
          <FormField
            control={form.control}
            name={'batch'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'Lote'} />
            )}
          />
          <FormField
            control={form.control}
            name={'shelfLifeExpirationDate'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={expirationRangeOptions.singleSelectOptions}
                label={'Vencimento'}
                clearable
                portal={false}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'deliveryForecastDate'}
            render={({ field }) => (
              <DateRangePickerGs
                selected={{
                  from: field.value?.from,
                  to: field.value?.to,
                }}
                onSelect={field.onChange}
                label={'Previsão de recebimento'}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'totalValue'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={valueRangeOptions.singleSelectOptions}
                label={'Valor'}
                clearable
                portal={false}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'productReasonTypeId'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={reasonOptions.singleSelectOptions}
                label={'Motivo'}
                clearable
                portal={false}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'storageLocation'}
            render={({ field }) => (
              <InputGs {...field} type="text" label={'SLOC'} />
            )}
          />
          <FormField
            control={form.control}
            name={'priority'}
            render={({ field }) => (
              <MultiSelectGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                label={'Prioridade'}
                maxCount={1}
                options={priorityOptions.multiSelectOptions}
                classNames={{
                  selectedBadge: SELECTED_BADGE_CLASS_NAME,
                }}
              />
            )}
          />
          <FormField
            control={form.control}
            name={'destinationPlant'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={destinationPlantOptions.singleSelectOptions}
                label={'Planta de destino'}
                clearable
                portal={false}
              />
            )}
          />
        </div>
        <div className={'flex gap-4 mt-6'}>
          <ButtonGs.Root
            type="button"
            variant="secondary"
            size="sm"
            onClick={resetForm}
          >
            Limpar filtros
          </ButtonGs.Root>
          <ButtonGs.Root type="submit" size="sm">
            Filtrar
          </ButtonGs.Root>
        </div>
      </form>
    </Form>
  );
};
